/**
 * Tiny pub/sub bridge so a clicked chat notification (created in the renderer)
 * can ask React to open a specific thread and reveal a specific message.
 *
 * The notification click handler lives in a plain module, while the page switch
 * (App) and the thread-open + scroll (useMessagesPage) live in React. This
 * mirrors the existing notification route-state bridge in the other direction.
 *
 * Keep this module dependency-free (no project imports) to avoid import cycles —
 * it is imported by the notification module, App, and the messages hook.
 */

let pendingTarget = null;
const listeners = new Set();

function normalizeTarget({ threadId, messageId, scope } = {}) {
  const tid = String(threadId || '').trim();
  if (!tid) {
    return null;
  }
  return {
    threadId: tid,
    messageId: String(messageId || '').trim(),
    // 'internal' targets open on the Chats page; anything else means patient Messages.
    // Three panes can own a thread: Messages (patient), Chats (staff) and Private (contact
    // chats). Anything unrecognised falls back to 'normal', which is where an unclassifiable
    // thread has always gone.
    scope: scope === 'internal' || scope === 'direct' ? scope : 'normal',
  };
}

/**
 * Publish a request to open `threadId` and scroll to `messageId`. The target is
 * remembered (so a not-yet-mounted Messages page can drain it on mount) and all
 * live subscribers are notified.
 */
export function requestOpenThreadMessage(target) {
  const next = normalizeTarget(target);
  if (!next) {
    return;
  }
  pendingTarget = next;
  for (const listener of listeners) {
    try {
      listener(next);
    } catch (_error) {
      // ignore listener failures
    }
  }
}

/**
 * Subscribe to open-thread requests. Returns an unsubscribe function.
 */
export function subscribeOpenThreadMessage(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Return and clear the pending target, if any. Used by the Messages/Chats panes to pick
 * up a request that fired before they mounted. Pass the pane's scope ('normal'|'internal')
 * so only the pane the target belongs to consumes it — without the filter, whichever pane
 * drains first would swallow (and lose) the other pane's jump.
 */
export function consumePendingOpenThreadMessage(scope) {
  if (!pendingTarget) {
    return null;
  }
  if (scope !== undefined && pendingTarget.scope !== scope) {
    return null;
  }
  const target = pendingTarget;
  pendingTarget = null;
  return target;
}

/**
 * Whether a target is buffered for `scope`, without consuming it. The persisted-selection restore
 * uses this to stand down on mount: the drain effect owns the selection in that case, and opening
 * the saved thread first would cost a second history fetch and a visible flash.
 */
export function hasPendingOpenThreadMessage(scope) {
  if (!pendingTarget) {
    return false;
  }
  return scope === undefined || pendingTarget.scope === scope;
}

// --- Dashboard variant: a clicked announcement notification/toast asks App to show the
// Dashboard page. No buffering needed — App (the sole subscriber) outlives every
// notification, and the Dashboard pane always exists.

const dashboardListeners = new Set();

export function requestOpenDashboard() {
  for (const listener of dashboardListeners) {
    try {
      listener();
    } catch (_error) {
      // ignore listener failures
    }
  }
}

export function subscribeOpenDashboard(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  dashboardListeners.add(listener);
  return () => {
    dashboardListeners.delete(listener);
  };
}
